import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { Plus, Send, FileEdit, Clock, AlertTriangle, Zap, Loader2 } from 'lucide-react'
import DataTable, { Column } from '@/components/DataTable'
import { useEffect, useState } from 'react'

interface Message {
  id: number
  subject: string
  body: string
  video_url: string | null
  status: string
  sender_email: string
  sender_name: string
  total_recipients: number
  sent_count: number
  failed_count: number
  pending_count: number
  in_flight: boolean
  sent_at: string | null
  created_at: string
  created_by_name: string
  triggered?: boolean
  trigger_name?: string
  target_agent_name?: string
  recipients?: Recipient[]
}

interface Recipient {
  id: number
  agent_id: number
  agent_name: string
  agent_email: string
  status: string
  error_message: string | null
  sent_at: string | null
}

interface Props extends PageProps {
  drafts: Message[]
  sent_messages: Message[]
  queued_messages: Message[]
  tab: string
}

export default function MessagesIndex({ drafts, sent_messages, queued_messages, tab }: Props) {
  const [activeTab, setActiveTab] = useState(tab || 'messages')

  const statusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    queued: 'bg-yellow-100 text-yellow-700',
    sending: 'bg-blue-100 text-blue-700',
    sent: 'bg-green-100 text-green-700',
    partially_sent: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700'
  }

  const statusLabels: Record<string, string> = {
    draft: 'Draft',
    queued: 'Queued',
    sending: 'Sending',
    sent: 'Sent',
    partially_sent: 'Partially sent',
    failed: 'Failed'
  }

  const anyInFlight = [...drafts, ...sent_messages, ...queued_messages].some((m) => m.in_flight)

  // A blast runs in the background — keep the list fresh while one is in flight
  // so the admin sees it finish without reloading the page.
  useEffect(() => {
    if (!anyInFlight) return
    const timer = setInterval(() => {
      router.reload({ only: ['drafts', 'sent_messages', 'queued_messages'] })
    }, 5000)
    return () => clearInterval(timer)
  }, [anyInFlight])

  const draftColumns: Column<Message>[] = [
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      render: (msg) => (
        <div>
          <p className="font-medium text-chestnut-charcoal">{msg.subject}</p>
          {msg.triggered && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 mt-0.5">
              <Zap className="h-3 w-3" />
              Auto-triggered: {msg.trigger_name}
            </span>
          )}
          {msg.video_url && (
            <span className="text-xs text-chestnut-forest mt-0.5 inline-block">Has video attachment</span>
          )}
        </div>
      )
    },
    {
      key: 'target_agent_name',
      header: 'Recipient',
      sortable: true,
      render: (msg) => msg.target_agent_name ? (
        <span className="text-sm text-chestnut-charcoal">{msg.target_agent_name}</span>
      ) : (
        <span className="text-sm text-chestnut-charcoal/50">All active agents</span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (msg) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusColors[msg.status]}`}>
          {statusLabels[msg.status] ?? msg.status}
        </span>
      )
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (msg) => new Date(msg.created_at).toLocaleDateString()
    }
  ]

  const sentColumns: Column<Message>[] = [
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      render: (msg) => (
        <div>
          <p className="font-medium text-chestnut-charcoal">{msg.subject}</p>
          {msg.video_url && (
            <span className="text-xs text-chestnut-forest mt-0.5 inline-block">Has video attachment</span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (msg) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap inline-flex items-center gap-1 ${statusColors[msg.status]}`}>
          {msg.in_flight && <Loader2 className="h-3 w-3 animate-spin" />}
          {statusLabels[msg.status] ?? msg.status}
        </span>
      )
    },
    {
      key: 'sent_count',
      header: 'Delivered',
      sortable: true,
      render: (msg) => (
        <div className="text-sm">
          <span className="text-green-600 font-medium">{msg.sent_count}</span>
          <span className="text-chestnut-charcoal/50"> / {msg.total_recipients}</span>
          {msg.failed_count > 0 && (
            <span className="text-red-600 ml-2">({msg.failed_count} failed)</span>
          )}
          {msg.pending_count > 0 && (
            <span className="text-blue-600 ml-2">({msg.pending_count} in progress)</span>
          )}
        </div>
      )
    },
    {
      key: 'sent_at',
      header: 'Sent',
      sortable: true,
      render: (msg) => msg.sent_at ? new Date(msg.sent_at).toLocaleDateString() : '-'
    }
  ]

  const queuedColumns: Column<Message>[] = [
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      render: (msg) => <p className="font-medium text-chestnut-charcoal">{msg.subject}</p>
    },
    {
      key: 'total_recipients',
      header: 'Recipients',
      sortable: true,
      render: (msg) => {
        const recipients = msg.recipients || []
        if (recipients.length === 0) return <span className="text-chestnut-charcoal/50">All active agents</span>
        return (
          <div className="text-sm">
            {recipients.slice(0, 3).map((r, i) => (
              <span key={r.id}>
                {i > 0 && ', '}
                {r.agent_name}
              </span>
            ))}
            {recipients.length > 3 && <span className="text-chestnut-charcoal/50"> +{recipients.length - 3} more</span>}
          </div>
        )
      }
    },
    {
      key: 'created_at',
      header: 'Queued',
      sortable: true,
      render: (msg) => new Date(msg.created_at).toLocaleDateString()
    }
  ]

  return (
    <AppLayout>
      <Head title="Messaging" />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Messaging</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Create and send email messages to agents</p>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/admin/commission_triggers" className="btn-secondary flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Commission Triggers
            </Link>
            <Link href="/admin/messages/new" className="btn-primary flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Compose Message
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card border-l-4 border-l-gray-400">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Drafts</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{drafts.length}</p>
          </div>
          <div className="card border-l-4 border-l-yellow-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Queued</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{queued_messages.length}</p>
          </div>
          <div className="card border-l-4 border-l-green-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Sent</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{sent_messages.length}</p>
          </div>
        </div>

        <div className="border-b border-chestnut-charcoal/10">
          <nav className="flex gap-6">
            {[
              { id: 'messages', label: 'Messages', icon: FileEdit },
              { id: 'queued', label: 'Queued', icon: Clock },
              { id: 'sent', label: 'Sent', icon: Send }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === id
                    ? 'border-chestnut-forest text-chestnut-forest'
                    : 'border-transparent text-chestnut-charcoal/60 hover:text-chestnut-charcoal'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
                {id === 'queued' && queued_messages.length > 0 && (
                  <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 text-xs rounded-full">{queued_messages.length}</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'messages' && (
          <div className="card p-6">
            <DataTable
              data={drafts}
              columns={draftColumns}
              searchPlaceholder="Search drafts..."
              searchKeys={['subject', 'created_by_name']}
              pageSize={10}
              emptyMessage="No draft messages. Click 'Compose Message' to create one."
              getRowKey={(msg) => msg.id}
              onRowClick={(msg) => router.visit(`/admin/messages/${msg.id}`)}
            />
          </div>
        )}

        {activeTab === 'queued' && (
          <div className="card p-6">
            {queued_messages.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3 mb-4">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  These messages are ready to be reviewed and sent. Click a message to review, edit, or send it.
                </p>
              </div>
            )}
            <DataTable
              data={queued_messages}
              columns={queuedColumns}
              searchPlaceholder="Search queued messages..."
              searchKeys={['subject']}
              pageSize={10}
              emptyMessage="No queued messages at this time."
              getRowKey={(msg) => msg.id}
              onRowClick={(msg) => router.visit(`/admin/messages/${msg.id}`)}
            />
          </div>
        )}

        {activeTab === 'sent' && (
          <div className="card p-6">
            <DataTable
              data={sent_messages}
              columns={sentColumns}
              searchPlaceholder="Search sent messages..."
              searchKeys={['subject']}
              pageSize={10}
              emptyMessage="No sent messages yet."
              getRowKey={(msg) => msg.id}
              onRowClick={(msg) => router.visit(`/admin/messages/${msg.id}`)}
            />
          </div>
        )}
      </div>
    </AppLayout>
  )
}
